/* Announcement Bar slider
   Lightweight accessible carousel for announcement bar sections.
   - Supports multiple instances on the page
   - Autoplay with pause/play, respects prefers-reduced-motion
   - Prev/Next controls
   - Pauses on hover/focus and after manual interaction
*/
(function(){
  if (window.AnnouncementBar) return; // avoid double-define

  var AnnouncementBar = {
    instances: {},

    initSection: function(options){
      var id = options.id;
      var selector = '[data-announcement-section-id="'+id+'"]';
      var root = document.querySelector(selector);
      if (!root) return;
      if (this.instances[id] && this.instances[id].state && this.instances[id].state.root === root){
        // already initialized for this DOM node
        return;
      }
      if (this.instances[id]){
        // re-init for a replaced node: clean up previous
        this.destroy(id);
      }

      var slider = root.querySelector('.announcement-slider');
      var track = root.querySelector('.announcement-slider__track');
      var slides = root.querySelectorAll('.announcement-slider__slide');
      var prevBtn = root.querySelector('.announcement-prev');
      var nextBtn = root.querySelector('.announcement-next');

      // Clone first and last slides for infinite loop effect (only if multiple slides)
      var slideCount = slides.length || 0;
      if (slideCount > 1) {
        var firstClone = slides[0].cloneNode(true);
        var lastClone = slides[slideCount - 1].cloneNode(true);
        firstClone.setAttribute('aria-hidden', 'true');
        lastClone.setAttribute('aria-hidden', 'true');
        track.appendChild(firstClone);
        track.insertBefore(lastClone, slides[0]);
        // Update slides NodeList to include clones
        slides = root.querySelectorAll('.announcement-slider__slide');
      }

      var state = {
        id: id,
        root: root,
        slider: slider,
        track: track,
        slides: slides,
        prevBtn: prevBtn,
        nextBtn: nextBtn,
        index: 1, // start at 1 because of prepended clone
        count: slideCount,
        realIndex: 0, // actual slide index (0-based)
        autoplay: !!options.enableAutoplay,
        interval: (options.interval && Number(options.interval)) || 5,
        timer: null,
        manualInteraction: false,
        listeners: [],
        pausedByUser: false,
        pausedTransient: false
      };

      // Helpers
      function setIndex(i, withAnimation, direction){
        if (state.count <= 1) return;
        
        var targetIndex = i;
        
        if (withAnimation) {
          track.style.transition = '';
        } else {
          track.style.transition = 'none';
        }
        
        state.index = targetIndex;
        var px = -state.index * 100;
        track.style.transform = 'translateX(' + px + '%)';
        
        // After animation completes, check if we need to snap to real position
        if (withAnimation) {
          setTimeout(function(){
            // If at first clone (beyond last real slide), snap to real first slide
            if (state.index === state.count + 1) {
              track.style.transition = 'none';
              state.index = 1;
              track.style.transform = 'translateX(-100%)';
              state.realIndex = 0;
            }
            // If at last clone (before first real slide), snap to real last slide  
            else if (state.index === 0) {
              track.style.transition = 'none';
              state.index = state.count;
              track.style.transform = 'translateX(' + (-state.count * 100) + '%)';
              state.realIndex = state.count - 1;
            }
            else {
              state.realIndex = state.index - 1;
            }
            updateSlideVisibility();
          }, 350); // match CSS transition duration
        } else {
          state.realIndex = state.index - 1;
        }
        
        updateSlideVisibility();
      }

      function updateSlideVisibility(){
        // Update slide visibility for screen readers: mark non-active slides aria-hidden
        state.slides.forEach(function(slide, idx){
          // The real active slide is at state.index (accounting for prepended clone)
          // First and last slides are clones and should stay hidden
          if (idx === state.index && idx !== 0 && idx !== state.count + 1) {
            slide.removeAttribute('aria-hidden');
            // Make interactive elements in active slide focusable
            slide.querySelectorAll('a,button,input,textarea,select').forEach(function(el){ el.tabIndex = 0; });
          } else {
            slide.setAttribute('aria-hidden', 'true');
            // Make interactive elements in inactive slides unfocusable
            slide.querySelectorAll('a,button,input,textarea,select').forEach(function(el){ el.tabIndex = -1; });
          }
        });
        
        // Use realIndex for live region announcements
        var activeSlide = state.slides[state.index];
        if (activeSlide && state.realIndex >= 0 && state.realIndex < state.count) {
          var live = document.getElementById('announcement-live-' + state.id);
          if (live) {
            live.textContent = activeSlide.innerText.trim();
          }
        }
      }

      function next(){ setIndex(state.index + 1, true, 'next'); }
      function prev(){ setIndex(state.index - 1, true, 'prev'); }

      function updatePlayPauseLabel(){
        var wrapper = root.closest('.announcement-bar--wrap');
        if (!wrapper) return;
        
        if (state.pausedByUser) {
          // User explicitly paused - show play button
          wrapper.classList.add('announcement-bar--is-paused');
        } else {
          // Playing or transiently paused - show pause button
          wrapper.classList.remove('announcement-bar--is-paused');
        }
      }

      function play(){
        if (state.timer) return;
        state.pausedByUser = false;
        state.pausedTransient = false;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        state.timer = setInterval(function(){ next(); }, state.interval * 1000);
        updatePlayPauseLabel();
      }

      function pause(userInitiated){
        // userInitiated=true means the user explicitly paused (click or prev/next).
        if (state.timer){ clearInterval(state.timer); state.timer = null; }
        if (userInitiated) {
          state.pausedByUser = true;
          // mark manualInteraction so autoplay doesn't auto-resume after transient events
          state.manualInteraction = true;
        } else {
          state.pausedTransient = true;
        }
        updatePlayPauseLabel();
      }

      // Event bindings — keep references so we can remove them on destroy
      if (nextBtn) {
        var onNext = function(e){ e.preventDefault(); next(); };
        var onNextKey = function(e){ if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); if (e.key === 'ArrowRight') next(); else prev(); } };
        nextBtn.addEventListener('click', onNext);
        nextBtn.addEventListener('keydown', onNextKey);
        state.listeners.push({ node: nextBtn, type: 'click', fn: onNext });
        state.listeners.push({ node: nextBtn, type: 'keydown', fn: onNextKey });
      }
      if (prevBtn) {
        var onPrev = function(e){ e.preventDefault(); prev(); };
        var onPrevKey = function(e){ if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); if (e.key === 'ArrowRight') next(); else prev(); } };
        prevBtn.addEventListener('click', onPrev);
        prevBtn.addEventListener('keydown', onPrevKey);
        state.listeners.push({ node: prevBtn, type: 'click', fn: onPrev });
        state.listeners.push({ node: prevBtn, type: 'keydown', fn: onPrevKey });
      }
      var playBtn = root.querySelector('.announcement-control--play');
      var pauseBtn = root.querySelector('.announcement-control--pause');
      
      if (playBtn) {
        var onPlay = function(e){ 
          e.preventDefault(); 
          play(); 
          if (pauseBtn) pauseBtn.focus();
        };
        playBtn.addEventListener('click', onPlay);
        state.listeners.push({ node: playBtn, type: 'click', fn: onPlay });
      }
      if (pauseBtn) {
        var onPause = function(e){ 
          e.preventDefault(); 
          pause(true); 
          if (playBtn) playBtn.focus();
        };
        pauseBtn.addEventListener('click', onPause);
        state.listeners.push({ node: pauseBtn, type: 'click', fn: onPause });
      }

      var onMouseEnter = function(){ if (state.timer) pause(false); };
      var onMouseLeave = function(){ if (state.pausedTransient && !state.pausedByUser && state.autoplay) { state.pausedTransient = false; play(); } };
      var onFocusIn = function(){ if (state.timer) pause(false); };
      var onFocusOut = function(){ if (state.pausedTransient && !state.pausedByUser && state.autoplay) { state.pausedTransient = false; play(); } };

      root.addEventListener('mouseenter', onMouseEnter);
      root.addEventListener('mouseleave', onMouseLeave);
      root.addEventListener('focusin', onFocusIn);
      root.addEventListener('focusout', onFocusOut);

      state.listeners.push({ node: root, type: 'mouseenter', fn: onMouseEnter });
      state.listeners.push({ node: root, type: 'mouseleave', fn: onMouseLeave });
      state.listeners.push({ node: root, type: 'focusin', fn: onFocusIn });
      state.listeners.push({ node: root, type: 'focusout', fn: onFocusOut });

      // Initialize position (starting at index 1 because of prepended clone)
      setIndex(1, false);

      // Set initial state for controls
      updatePlayPauseLabel();
      
      if (state.count <= 1) {
        if (prevBtn) prevBtn.setAttribute('aria-disabled', 'true');
        if (nextBtn) nextBtn.setAttribute('aria-disabled', 'true');
      }

      // Respect reduced motion
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches){
        // Do not autoplay
        state.autoplay = false;
      }

      // Reset transition after initial positioning to allow animations
      setTimeout(function(){ 
        if (track) track.style.transition = ''; 
      }, 10);

      // Start autoplay if enabled
      if (state.autoplay && state.count > 1){ play(); }

      // Save instance
      this.instances[id] = { state: state, next: next, prev: prev, play: play, pause: pause };
    },

    destroy: function(id){
      var inst = this.instances[id];
      if (!inst) return;
      if (inst.state.timer) clearInterval(inst.state.timer);
      // Remove event listeners we attached
      if (inst.state.listeners && inst.state.listeners.length) {
        inst.state.listeners.forEach(function(l){
          try { l.node.removeEventListener(l.type, l.fn); } catch (e) { /* ignore */ }
        });
      }
      // Clear references
      delete this.instances[id];
    }
  };

  window.AnnouncementBar = AnnouncementBar;
})();
